import React, { useEffect, useRef, useState } from "react";
import { Plus, X, Wand2, Square, Copy, History, Loader2, Paperclip, FileText, Film, Send, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { ponytailApps, ponytailCraft, ponytailHistory, ponytailUpload, ponytailJob, ponytailChat, ponytailChatGet } from "../lib/api";

const PINK = "#F472B6";
const EXTS = ["png", "jpg", "jpeg", "webp", "gif", "pdf", "docx", "xlsx", "txt", "md", "csv", "json", "log", "mp4", "mov", "webm"];
const VIDS = ["mp4", "mov", "webm"];
const ACCEPT = EXTS.map((e) => `.${e}`).join(",");
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

const newSid = () => `pt${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const PonytailThread = ({ uid, app, onResult }) => {
  const [sid, setSid] = useState(() => { try { return localStorage.getItem("ponytail_sid") || newSid(); } catch (e) { return newSid(); } });
  const [msgs, setMsgs] = useState([]);
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const endRef = useRef(null);
  useEffect(() => {
    try { localStorage.setItem("ponytail_sid", sid); } catch (e) {}
    ponytailChatGet(uid, sid).then((d) => setMsgs(d.messages || [])).catch(() => {});
  }, [uid, sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "nearest" }); }, [msgs, busy]);
  const send = async (generate = false) => {
    const m = txt.trim();
    if ((!m && !generate) || busy) return;
    if (m) setMsgs((x) => [...x, { role: "user", text: m }]);
    setTxt(""); setBusy(true);
    try {
      let d = await ponytailChat({ user_id: uid, session_id: sid, message: m, generate, app });
      while (d.job_id) {
        await new Promise((ok) => setTimeout(ok, 3000));
        const j = await ponytailJob(uid, d.job_id);
        if (j.status === "error") throw new Error(j.error);
        if (j.status === "done") d = j.result;
      }
      setMsgs(d.messages || []); setReady(d.ready);
      if (d.result) onResult(d.result);
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Ponytail couldn't reply"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border bg-[#0d0612] p-3 space-y-2" style={{ borderColor: `${PINK}33` }} data-testid="ponytail-thread">
      <div className="flex items-center justify-between">
        <p className="nx-mono text-[11px] tracking-widest text-pink-300/70">TALK IT THROUGH WITH PONYTAIL</p>
        <button onClick={() => { setSid(newSid()); setMsgs([]); setReady(false); }} data-testid="ponytail-new-chat" className="nx-mono text-[11px] text-pink-200/70 hover:text-pink-50 flex items-center gap-1"><MessageSquarePlus className="w-3.5 h-3.5" /> New chat</button>
      </div>
      <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="ponytail-thread-messages">
        {!msgs.length && <p className="text-xs text-pink-100/40">Say what you want in your own words. I'll ask at most 2 questions, then say "build it".</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <p className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.role === "user" ? "bg-pink-400/20 text-pink-50 rounded-br-sm" : "bg-[#1a0d22] text-pink-100 border border-pink-300/15 rounded-bl-sm"}`} data-testid={`ponytail-thread-msg-${i}`}>{m.text}</p>
          </div>))}
        {busy && <p className="text-xs text-pink-200/60 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Ponytail is thinking…</p>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2">
        <input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} data-testid="ponytail-thread-input"
          placeholder='Talk to Ponytail… (say "build it" when ready)' className="flex-1 min-w-0 bg-[#14081a] text-pink-50 border rounded-xl px-3 py-2 text-sm focus:outline-none" style={{ borderColor: `${PINK}44` }} />
        <button onClick={() => send()} disabled={busy || !txt.trim()} data-testid="ponytail-thread-send" className="px-3 rounded-xl border text-pink-200 disabled:opacity-40" style={{ borderColor: `${PINK}44` }}><Send className="w-4 h-4" /></button>
      </div>
      <button onClick={() => send(true)} disabled={busy || !msgs.some((m) => m.role === "user")} data-testid="ponytail-thread-generate"
        className={`w-full py-2 rounded-xl font-bold nx-orbitron tracking-widest text-sm flex items-center justify-center gap-2 disabled:opacity-40 ${ready ? "text-[#030712]" : "text-pink-100 border"}`} style={ready ? { background: PINK } : { borderColor: `${PINK}55` }}>
        <Wand2 className="w-4 h-4" /> Generate prompt{ready ? " · ready" : ""}
      </button>
    </div>
  );
};

const Chip = ({ f, onRemove, testid }) => (
  <a href={f.url} target="_blank" rel="noreferrer" data-testid={testid} title={f.name}
    className="flex items-center gap-2 max-w-[14rem] rounded-lg px-2 py-1 bg-pink-400/10 border border-pink-300/20 text-xs text-pink-50 hover:border-pink-300/50">
    {f.kind === "video" ? <Film className="w-4 h-4 shrink-0 text-pink-300" /> : f.kind === "image" ? <img src={f.url} alt={f.name} className="w-7 h-7 rounded object-cover shrink-0" /> : <FileText className="w-4 h-4 shrink-0 text-pink-300" />}
    <span className="truncate">{f.name}</span>
    <span className="nx-mono text-pink-200/50 shrink-0">{fmtSize(f.size)}</span>
    {onRemove && <button onClick={(e) => { e.preventDefault(); onRemove(); }} data-testid={`${testid}-remove`} className="text-pink-200/60 hover:text-rose-400 shrink-0"><X className="w-3.5 h-3.5" /></button>}
  </a>
);

const Avatar = ({ busy }) => (
  <div className="relative w-40 h-40 sm:w-56 sm:h-56 shrink-0" data-testid="ponytail-avatar">
    <div className="absolute inset-0 rounded-full nx-spin-slow" style={{ border: `1px dashed ${PINK}66` }} />
    <div className="absolute inset-3 rounded-full overflow-hidden" style={{ boxShadow: `0 0 50px ${PINK}${busy ? "aa" : "55"}` }}>
      <img src="/ponytail-brain.jpg" alt="Ponytail brain" className={`w-full h-full object-cover ${busy ? "animate-pulse" : ""}`} style={{ animation: busy ? undefined : "ptFloat 6s ease-in-out infinite" }} />
      <div className="absolute inset-0 nx-scanlines opacity-40" />
    </div>
    <style>{"@keyframes ptFloat { 0%,100% { transform: translateY(0) scale(1.02) } 50% { transform: translateY(-6px) scale(1.05) } }"}</style>
  </div>
);

const Result = ({ r }) => {
  const copy = () => navigator.clipboard.writeText(r.prompt).then(() => toast.success("Prompt copied. Paste it into Emergent."));
  return (
    <div className="rounded-2xl border p-4 space-y-3 nx-fade-up" style={{ borderColor: `${PINK}55`, background: "#14081a" }} data-testid="ponytail-result">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="nx-orbitron font-bold tracking-wider" style={{ color: PINK }}>{r.title}</p>
        <span className="nx-mono text-[11px] px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300" data-testid="ponytail-savings">~{r.savings_pct}% fewer credits</span>
        <span className="nx-mono text-[11px] text-pink-200/60">{r.merged} asks → 1 prompt · {r.app}</span>
        <button onClick={copy} data-testid="ponytail-copy" className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-full font-bold text-[#030712]" style={{ background: PINK }}><Copy className="w-4 h-4" /> Copy</button>
      </div>
      {r.questions?.length > 0 && <div className="text-amber-200 text-sm space-y-1" data-testid="ponytail-questions">{r.questions.map((q, i) => <p key={i}>? {q}</p>)}</div>}
      {r.generated_at && <p className="nx-mono text-[11px] text-pink-200/50" data-testid="ponytail-generated-at">Generated {new Date(r.generated_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>}
      <pre className="whitespace-pre-wrap text-[13px] leading-relaxed text-pink-50/90 font-mono max-h-[45vh] overflow-y-auto" data-testid="ponytail-prompt">{r.prompt}</pre>
      {r.attachments?.length > 0 && <div className="flex flex-wrap gap-2" data-testid="ponytail-result-attachments">{r.attachments.map((f) => <Chip key={f.id} f={f} testid={`ponytail-hist-att-${f.id}`} />)}</div>}
      {r.videos?.length > 0 && <div className="space-y-1" data-testid="ponytail-result-videos">{r.videos.map((v, i) => <a key={i} href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-pink-100/80 hover:text-pink-50"><Film className="w-3.5 h-3.5 shrink-0" style={{ color: PINK }} /><span className="truncate">{v.name}</span><span className="nx-mono text-pink-200/50 shrink-0">{v.status}</span></a>)}</div>}
      {r.why?.length > 0 && <ul className="text-xs text-pink-200/60 list-disc pl-5 space-y-0.5">{r.why.map((w, i) => <li key={i}>{w}</li>)}</ul>}
    </div>
  );
};

export default function NexusPonytail({ session }) {
  const uid = session.user_id;
  const [asks, setAsks] = useState([]);
  const [text, setText] = useState("");
  const [app, setApp] = useState("auto");
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hist, setHist] = useState([]);
  const [showHist, setShowHist] = useState(false);
  const genRef = useRef(0);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [drag, setDrag] = useState(false);
  const pickRef = useRef(null);

  const attach = async (list) => {
    const arr = Array.from(list || []);
    if (!arr.length) return;
    setErr("");
    if (files.length + arr.length > 5) return setErr("Max 5 attachments.");
    const bad = arr.find((f) => !EXTS.includes((f.name.split(".").pop() || "").toLowerCase()));
    if (bad) return setErr(`${bad.name}: type not allowed. Use ${EXTS.join(", ")}.`);
    const big = arr.find((f) => f.size > (VIDS.includes((f.name.split(".").pop() || "").toLowerCase()) ? 100 : 10) * 1024 * 1024);
    if (big) return setErr(`${big.name}: too large (10 MB files, 100 MB videos).`);
    setUploading(true);
    try { const d = await ponytailUpload(uid, arr); setFiles((x) => [...x, ...(d.files || [])]); }
    catch (e) { setErr(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (pickRef.current) pickRef.current.value = ""; }
  };

  useEffect(() => {
    ponytailApps(uid).then((d) => setApps(d.apps || [])).catch(() => {});
    ponytailHistory(uid).then((d) => setHist(d.items || [])).catch(() => {});
  }, [uid]);

  const add = () => { const t = text.trim(); if (!t) return; if (/^(stop|cancel)$/i.test(t)) { setText(""); stop(); return; } setAsks((a) => [...a, t]); setText(""); };
  const stop = () => { genRef.current += 1; setBusy(false); toast.message("Stopped. Edit your asks and try again."); };
  const craft = async () => {
    const list = text.trim() && !/^(stop|cancel)$/i.test(text.trim()) ? [...asks, text.trim()] : asks;
    if (!list.length) return toast.error("Add at least one ask");
    const gen = ++genRef.current;
    setAsks(list); setText(""); setBusy(true); setResult(null);
    try {
      let r = await ponytailCraft(uid, list, app, files.map((f) => f.id));
      while (r.job_id && gen === genRef.current) {
        await new Promise((ok) => setTimeout(ok, 4000));
        const j = await ponytailJob(uid, r.job_id);
        if (j.status === "error") throw new Error(j.error);
        if (j.status === "done") r = j.result;
      }
      if (gen !== genRef.current) return;
      setResult(r); setHist((h) => [r, ...h]); setAsks([]); setFiles([]); setErr("");
    } catch (e) { if (gen === genRef.current) toast.error(e?.response?.data?.detail || e?.message || "Ponytail could not write it"); }
    finally { if (gen === genRef.current) setBusy(false); }
  };

  return (
    <div className="h-full overflow-y-auto bg-[#07030a] p-4 sm:p-6" data-testid="ponytail-page">
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-6">
        <div className="flex flex-col items-center gap-3 sm:w-64 shrink-0">
          <Avatar busy={busy} />
          <p className="nx-orbitron font-black tracking-[0.3em]" style={{ color: PINK }}>PONYTAIL</p>
          <p className="text-xs text-center text-pink-100/60 max-w-[15rem]">Drop every rough ask here. I merge them into one exact prompt, so the builder doesn't explore, ask or re-test, which saves 50-70% of your credits.</p>
          <button onClick={() => setShowHist((s) => !s)} data-testid="ponytail-history-toggle" className="nx-mono text-[11px] text-pink-200/70 flex items-center gap-1"><History className="w-3.5 h-3.5" /> {showHist ? "Hide" : "Show"} past prompts ({Math.min(hist.length, 5)})</button>
        </div>
        <div className="flex-1 min-w-0 space-y-4">
          <select value={app} onChange={(e) => setApp(e.target.value)} data-testid="ponytail-app" className="bg-[#14081a] border rounded-xl px-3 py-2 text-sm text-pink-50" style={{ borderColor: `${PINK}44` }}>
            <option value="auto">Auto-detect app</option>
            {apps.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <PonytailThread uid={uid} app={app} onResult={(r) => { setResult(r); setHist((h) => [r, ...h]); }} />
          {asks.length > 0 && (
            <div className="space-y-2" data-testid="ponytail-asks">
              {asks.map((a, i) => (
                <div key={i} className="flex items-start gap-2 rounded-xl px-3 py-2 bg-pink-400/5 border border-pink-300/15 text-sm text-pink-50">
                  <span className="nx-mono text-pink-300/60">{i + 1}.</span><span className="flex-1">{a}</span>
                  <button onClick={() => setAsks((x) => x.filter((_, j) => j !== i))} data-testid={`ponytail-ask-remove-${i}`} className="text-pink-200/50 hover:text-rose-400"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          <div className={`flex gap-2 rounded-xl transition-shadow ${drag ? "ring-2 ring-pink-400 bg-pink-400/10" : ""}`} data-testid="ponytail-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); attach(e.dataTransfer.files); }}>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} data-testid="ponytail-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); add(); } }}
              placeholder="Type a rough ask, then Enter to add another… paste a YouTube / Facebook / Instagram link and say what you want from it"
              className="flex-1 min-w-0 bg-[#14081a] text-pink-50 border rounded-xl px-3 py-2 text-sm focus:outline-none" style={{ borderColor: `${PINK}44` }} />
            <button onClick={add} data-testid="ponytail-add" className="px-3 rounded-xl border text-pink-200" style={{ borderColor: `${PINK}44` }}><Plus className="w-5 h-5" /></button>
            <button onClick={() => pickRef.current?.click()} disabled={uploading} title="Attach images or files" data-testid="ponytail-attach" className="px-3 rounded-xl border text-pink-200" style={{ borderColor: `${PINK}44` }}>{uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}</button>
            <input ref={pickRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="ponytail-file-input" onChange={(e) => attach(e.target.files)} />
          </div>
          {drag && <p className="text-xs text-pink-200/70">Drop files to attach</p>}
          {err && <p className="text-xs text-rose-400" data-testid="ponytail-attach-error">{err}</p>}
          {files.length > 0 && <div className="flex flex-wrap gap-2" data-testid="ponytail-chips">{files.map((f) => <Chip key={f.id} f={f} testid={`ponytail-chip-${f.id}`} onRemove={() => setFiles((x) => x.filter((y) => y.id !== f.id))} />)}</div>}
          {busy ? (
            <button onClick={stop} data-testid="ponytail-stop" className="w-full py-3 rounded-xl bg-[#EF4444] text-white font-bold nx-orbitron tracking-widest flex items-center justify-center gap-2"><Square className="w-4 h-4 fill-white" /> STOP</button>
          ) : (
            <button onClick={craft} data-testid="ponytail-craft" className="w-full py-3 rounded-xl font-bold nx-orbitron tracking-widest text-[#030712] flex items-center justify-center gap-2" style={{ background: PINK }}>
              <Wand2 className="w-5 h-5" /> Write my prompt {asks.length + (text.trim() ? 1 : 0) > 1 ? `(merge ${asks.length + (text.trim() ? 1 : 0)})` : ""}
            </button>
          )}
          {busy && <p className="text-sm text-pink-200/70 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Ponytail is watching any videos and tightening your prompt…</p>}
          {result && <Result r={result} />}
          {showHist && <div className="space-y-3" data-testid="ponytail-history">{hist.slice(0, 5).filter((h) => h.id !== result?.id).map((h) => <Result key={h.id} r={h} />)}</div>}
        </div>
      </div>
    </div>
  );
}
